import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './UserManagement.css'

const ROLE_LABELS = {
  owner:        'Owner',
  tenant_admin: 'Admin',
  user:         'Benutzer',
}

const UserManagement = () => {
  const { apiFetch, isOwner, admin } = useAuth()

  const [tenants,        setTenants]        = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [users,          setUsers]          = useState([])
  const [loading,        setLoading]        = useState(false)
  const [error,          setError]          = useState(null)
  const [success,        setSuccess]        = useState(null)
  const [showAdd,        setShowAdd]        = useState(false)
  const [addForm,        setAddForm]        = useState({ email: '', role: 'user' })
  const [saving,         setSaving]         = useState(false)

  // Load tenants for owner
  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants').then(r => r.json()).then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        if (list.length > 0) setSelectedTenant(list[0].id)
      }).catch(() => {})
    } else {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner]) // intentional — runs once on mount only

  // Load users when tenant changes
  const loadUsers = () => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    apiFetch('/api/admin/admins')
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : []
        setUsers(isOwner
          ? list.filter(u => u.tenant_id === selectedTenant)
          : list
        )
      })
      .catch(() => setError('Benutzer konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }

  useEffect(loadUsers, [selectedTenant])

  const showSuccess = (msg) => {
    setSuccess(msg)
    setTimeout(() => setSuccess(null), 3000)
  }

  const addUser = async () => {
    const email = addForm.email.trim().toLowerCase()
    if (!email) return
    setSaving(true)
    setError(null)
    const res = await apiFetch('/api/admin/admins', {
      method: 'POST',
      body: JSON.stringify({ email, role: addForm.role, tenant_id: selectedTenant }),
    })
    if (res.ok) {
      const user = await res.json()
      setUsers(prev => [user, ...prev])
      setAddForm({ email: '', role: 'user' })
      setShowAdd(false)
      showSuccess('Benutzer hinzugefügt')
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Fehler beim Hinzufügen')
    }
    setSaving(false)
  }

  const deleteUser = async (id) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return
    const res = await apiFetch(`/api/admin/admins/${id}`, { method: 'DELETE' })
    if (res.ok) {
      setUsers(prev => prev.filter(u => u.id !== id))
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Fehler beim Löschen')
    }
  }

  const changeRole = async (id, role) => {
    const res = await apiFetch(`/api/admin/admins/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })
    if (res.ok) {
      const updated = await res.json()
      setUsers(prev => prev.map(u => u.id === id ? updated : u))
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Fehler beim Ändern der Rolle')
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Benutzerverwaltung</h1>
          <p>Dashboard-Zugänge für diesen Mandanten verwalten</p>
        </div>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      {/* Tenant selector — owner only */}
      {isOwner && tenants.length > 0 && (
        <div className="card um-tenant-card">
          <div className="form-group">
            <label>Mandant</label>
            <select className="form-control" value={selectedTenant}
              onChange={e => setSelectedTenant(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="card">
        <div className="um-list-header">
          <div className="section-title">Benutzer</div>
          {isOwner && !showAdd && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
              + Benutzer hinzufügen
            </button>
          )}
        </div>

        {/* Add form — owner only */}
        {isOwner && showAdd && (
          <div className="um-add-form">
            <div className="um-add-fields">
              <div className="form-group">
                <label>E-Mail-Adresse</label>
                <input
                  className="form-control"
                  placeholder="name@unternehmen.de"
                  value={addForm.email}
                  onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addUser()}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Rolle</label>
                <select className="form-control" value={addForm.role}
                  onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}>
                  {isOwner && <option value="tenant_admin">Admin</option>}
                  <option value="user">Benutzer</option>
                </select>
              </div>
            </div>
            <div className="um-add-actions">
              <button className="btn btn-primary btn-sm" onClick={addUser}
                disabled={saving || !addForm.email.trim()}>
                {saving ? <><span className="spinner spinner-sm" /> Hinzufügen…</> : 'Hinzufügen'}
              </button>
              <button className="btn btn-ghost btn-sm"
                onClick={() => { setShowAdd(false); setAddForm({ email: '', role: 'user' }) }}>
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* User list */}
        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : users.length === 0 ? (
          <div className="um-empty">Noch keine Benutzer eingetragen</div>
        ) : (
          <table className="um-table">
            <thead>
              <tr>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Hinzugefügt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td className="um-email">{u.email}</td>
                  <td>
                    {u.role === 'owner' || u.email === admin?.email ? (
                      <span className={`um-badge um-badge--${u.role}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    ) : (
                      <select
                        className="um-role-select"
                        value={u.role}
                        onChange={e => changeRole(u.id, e.target.value)}
                      >
                        {isOwner && <option value="tenant_admin">Admin</option>}
                        <option value="user">Benutzer</option>
                      </select>
                    )}
                  </td>
                  <td className="um-date">
                    {new Date(u.created_at).toLocaleDateString('de-DE')}
                  </td>
                  <td>
                    {u.email !== admin?.email && u.role !== 'owner' && (
                      <button className="btn btn-ghost btn-sm um-btn-delete"
                        onClick={() => deleteUser(u.id)}>
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedTenant && (
        <VersionHistoryPanel table="admin_users" tenantId={selectedTenant} onRestored={loadUsers} />
      )}
    </div>
  )
}

export default UserManagement
